import { createAddressTitle, type HomeFeedCard } from '@aikoa/common'
import { getReadinessFromAnswers } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { memo, useMemo } from 'react'
import { Pressable, View } from 'react-native'
import {
  createHighlightItemsFromAssets,
  createHighlightItemsFromLeads,
  FeedHighlightItems,
  type MatchingLeadHighlight,
} from '~/components/feed/FeedHighlights'
import Icon from '~/components/Icon'
import { LeadImage } from '~/components/leads/LeadListItem'
import { Detail } from '~/components/typography/Content'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import { PropertyTypeBadge } from '~/components/ui/TypeBadge'
import { cn } from '~/lib/utils'

type HomeFeedCardProps = {
  card: HomeFeedCard
  cardIndex: number
  totalCards: number
  onOpen: (card: HomeFeedCard) => void
  onNext: (card: HomeFeedCard) => void
}

function isAssetCard(
  card: HomeFeedCard
): card is Extract<HomeFeedCard, { kind: 'asset_match' | 'asset_added' }> {
  return card.kind === 'asset_match' || card.kind === 'asset_added'
}

function CardImage({ card }: { card: HomeFeedCard }) {
  if (isAssetCard(card)) {
    const mediaUrl = card.asset.media?.[0]?.url ?? null
    return (
      <LeadImage
        mediaUrl={mediaUrl}
        className="h-full w-full overflow-hidden rounded-t-content-inner p-0"
      />
    )
  }

  const topAsset = card.matchingAssets?.[0]?.asset
  const mediaUrl = topAsset?.media?.[0]?.url ?? null
  return (
    <LeadImage
      mediaUrl={mediaUrl}
      className="h-full w-full overflow-hidden rounded-t-content-inner p-0"
    />
  )
}

function CardHeadline({ card }: { card: HomeFeedCard }) {
  const { t } = useTranslations('feed')
  const icon = card.kind.startsWith('lead_') ? 'target' : 'home-plus-outline'

  return (
    <View className="mb-auto flex-row items-center gap-2 self-start rounded-full bg-secondary pr-3">
      <View className={cn('size-8 items-center justify-center rounded-full bg-primary')}>
        <Icon name={icon} library="materialcommunityicons" size={16} className="text-white" />
      </View>
      <Text className="drop-shadow-primary font-medium text-xs uppercase tracking-widest text-background">
        {t(($) => $.stack.card[card.kind].headline)}
      </Text>
    </View>
  )
}

function CardTitle({ card }: { card: HomeFeedCard }) {
  let content: string

  if (isAssetCard(card)) {
    content = createAddressTitle(card.asset.address, false) || card.asset.name
  } else {
    const { lead } = card
    content = lead.is_own || lead.is_purchased ? lead.name || 'Contact' : 'Verified Buyer'
  }

  return (
    <Text className="font-semibold text-xl/relaxed text-white" numberOfLines={2}>
      {content}
    </Text>
  )
}

function CardTypeBadge({ card }: { card: HomeFeedCard }) {
  if (isAssetCard(card)) {
    const relationType = card.asset.relationType ?? 'representation'
    const ownership = card.asset.isOwn ? 'own' : 'organisation'

    return (
      <PropertyTypeBadge
        type={relationType}
        ownership={ownership}
        organisationName={card.asset.organisation?.name}
        realtorName={card.asset.organisation?.realtorName}
        variant="inverted"
        size="sm"
        className="self-start"
        textClassName="text-tertiary-foreground!"
      />
    )
  }

  return null
}

function CardHighlights({ card, hiddenCount }: { card: HomeFeedCard; hiddenCount: number }) {
  const highlightItems = useMemo(() => {
    if (isAssetCard(card)) {
      const leads: MatchingLeadHighlight[] = card.leads.map((item) => ({
        leadId: item.lead.id,
        leadName: item.lead.name ?? '',
        matchScores: item.matchScore
          ? {
              asset_id: item.matchScore.assetId,
              match_score: item.matchScore.matchScore,
              location_score: item.matchScore.locationScore,
              price_score: item.matchScore.priceScore,
              size_score: item.matchScore.sizeScore,
              building_type_score: item.matchScore.buildingTypeScore,
              other_score: item.matchScore.otherScore,
            }
          : null,
        feedbackQualification: item.feedback
          ? (getReadinessFromAnswers(item.feedback.answers) ?? null)
          : null,
        isNew: item.isNew,
        itemDate: card.feedAddedAt,
        isOwn: item.lead.is_own ?? false,
        isPurchased: item.lead.is_purchased ?? false,
      }))

      return createHighlightItemsFromLeads({ leads, maxTypeItems: 2 })
    }

    return createHighlightItemsFromAssets({
      matchingAssets: card.matchingAssets.map((match) => ({
        assetId: match.asset.id,
        assetName: match.asset.name,
        address: match.asset.address,
        matchScores: match.matchScore
          ? {
              asset_id: match.asset.id,
              match_score: match.matchScore.matchScore,
              location_score: match.matchScore.locationScore,
              price_score: match.matchScore.priceScore,
              size_score: match.matchScore.sizeScore,
              building_type_score: match.matchScore.buildingTypeScore,
              other_score: match.matchScore.otherScore,
            }
          : null,
        isNew: match.isNew,
        itemDate: card.feedAddedAt,
        isOwn: match.asset.isOwn,
        relationType: match.asset.relationType,
        organisationName: match.asset.organisation?.name,
        realtorName: match.asset.organisation?.realtorName,
      })),
      maxTypeItems: 2,
    })
  }, [card])

  if (highlightItems.length === 0) {
    return null
  }

  return (
    <FeedHighlightItems
      items={highlightItems}
      maxItems={2}
      moreCount={hiddenCount}
      className="rounded-2xl bg-card"
      itemClassName="bg-transparent odd:bg-white first:rounded-t-2xl last:rounded-b-2xl"
      moreTextClassName="text-tertiary-foreground"
    />
  )
}

export const HomeFeedCardComponent = memo(function HomeFeedCardComponent({
  card,
  cardIndex,
  totalCards,
  onOpen,
  onNext,
}: HomeFeedCardProps) {
  const { t } = useTranslations('feed')

  const counterText = t(($) => $.stack.counter, {
    current: cardIndex + 1,
    total: totalCards,
  })

  const hasHighlights = isAssetCard(card) ? card.leads.length > 0 : card.matchingAssets.length > 0

  const totalCount = isAssetCard(card)
    ? (card.totalMatchingLeadsCount ?? 0)
    : card.kind === 'lead_match'
      ? (card.totalMatchingAssetsCount ?? 0)
      : 0

  const hiddenCount = hasHighlights ? Math.max(0, totalCount - 2) : 0

  return (
    <Pressable
      onPress={() => onOpen(card)}
      className="select-none rounded-content-inner transition-all duration-150 active:scale-[0.99]">
      <View className="overflow-hidden rounded-content-inner bg-primary">
        <View className="relative h-56 w-full overflow-hidden">
          <CardImage card={card} />
          <View className="bg-linear-to-t absolute inset-0 from-primary to-primary/0" />

          <View className="absolute right-4 top-4 flex-row items-center rounded-full bg-secondary px-2.5 py-1">
            <Detail className="font-medium text-xxs text-white">{counterText}</Detail>
          </View>

          <View className="absolute inset-x-0 bottom-2 top-4 px-4 pb-5">
            <CardHeadline card={card} />
            <CardTitle card={card} />
            <CardTypeBadge card={card} />
          </View>
        </View>
        {hasHighlights && (
          <View className="px-4 pb-2">
            <CardHighlights card={card} hiddenCount={hiddenCount} />
          </View>
        )}
        <View className="flex-row items-center gap-2 px-4 pb-4 pt-3">
          <Button
            variant="secondary"
            size="lg"
            onPress={() => onOpen(card)}
            className="flex-1 border-0 hover:bg-accent active:bg-accent">
            <ButtonLabel className="font-semibold text-white">
              {t(($) => $.actions.open)}
            </ButtonLabel>
          </Button>

          <Button
            variant="ghost"
            size="lg"
            onPress={() => onNext(card)}
            className="flex-row items-center gap-1 pr-5">
            <ButtonLabel
              className="font-normal text-white"
              icon={<Icon name="chevron-forward" size={13} className="order-2" />}>
              {t(($) => $.actions.next)}
            </ButtonLabel>
          </Button>
        </View>
      </View>
    </Pressable>
  )
})
